// ==========================================
// SelecionarTipo.jsx — V12 LENDÁRIO
// ✅ Estrutura de sessão/backup/navegação PRESERVADA
// ✅ Visual turbinado com cores BoraFrete
// ✅ Animações, gradientes, cards 3D
// ==========================================
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { 
  Truck, Building2, Loader2, ArrowRight, 
  CheckCircle2, Sparkles, Zap, TrendingUp,
  Shield, Clock, MapPin
} from 'lucide-react';

export default function SelecionarTipo() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const [hovered, setHovered] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // ✅ ESTRUTURA DE SESSÃO PRESERVADA (V11)
  useEffect(() => {
    const getUser = async () => {
      let session = null;
      let attempts = 0;
      const maxAttempts = 5;

      while (!session && attempts < maxAttempts) {
        const { data: { session: s }, error } = await supabase.auth.getSession();
        if (!error && s?.user) {
          session = s;
          break;
        }
        attempts++;
        if (attempts < maxAttempts) {
          console.log(`⏳ SelecionarTipo tentativa ${attempts}/${maxAttempts}`);
          await new Promise(resolve => setTimeout(resolve, 400));
        }
      }

      if (!session?.user) {
        console.log('🔄 SelecionarTipo: tentando refreshSession...');
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (refreshData?.session?.user) {
          session = refreshData.session;
        }
      }

      if (!session?.user) {
        console.error('❌ SelecionarTipo: Sem sessão');
        setError('Sessão expirada. Faça login novamente.');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
        return;
      }

      console.log('✅ SelecionarTipo: Usuário OK', session.user.id);
      setCurrentUser(session.user);

      // ✅ BACKUP PRESERVADO
      const backup = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: session.expires_at,
        saved_at: Date.now()
      };
      sessionStorage.setItem('borafrete_session_backup', JSON.stringify(backup));
      console.log('💾 Backup salvo no sessionStorage');
    };

    getUser();
  }, [navigate]);

  // ✅ HANDLE PRESERVADO (V11)
  const handleSelecionarTipo = async (tipo) => {
    if (!currentUser?.id) {
      setError('Usuário não autenticado.');
      return;
    }

    setLoading(tipo);
    setError('');

    try {
      console.log(`[SelecionarTipo] Salvando: ${tipo} para ${currentUser.id}`);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('[SelecionarTipo] Sem sessão, fazendo refresh...');
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (!refreshData?.session) {
          throw new Error('Sessão expirada. Faça login novamente.');
        }
      }

      const now = new Date().toISOString();
      const safeTipo = tipo.toLowerCase().trim();

      let saved = false;
      let retries = 0;
      
      while (!saved && retries < 3) {
        const { error } = await supabase
          .from('profiles')
          .upsert({
            id: currentUser.id,
            tipo: safeTipo,
            email: currentUser.email,
            nome: currentUser.user_metadata?.full_name || currentUser.email?.split('@')[0] || 'Usuário',
            updated_at: now
          }, { onConflict: 'id' });

        if (!error) {
          saved = true;
          console.log('[SelecionarTipo] Profile salvo!');
          break;
        }

        console.warn(`[SelecionarTipo] Retry ${retries + 1}:`, error);
        
        if (error.code === '401' || error.message?.includes('row-level security')) {
          await supabase.auth.refreshSession();
          await new Promise(resolve => setTimeout(resolve, 600));
        }
        
        retries++;
      }

      if (!saved) {
        throw new Error('Erro ao salvar perfil. Tente novamente.');
      }

      // ✅ CRIA REGISTRO NA TABELA ESPECÍFICA (PRESERVADO)
      if (safeTipo === 'empresa') {
        const { data: existe } = await supabase
          .from('empresas')
          .select('id')
          .eq('usuario_id', currentUser.id)
          .maybeSingle();

        if (!existe) {
          const { error: insertError } = await supabase.from('empresas').insert({
            usuario_id: currentUser.id,
            razao_social: currentUser.user_metadata?.full_name || 'Empresa',
            email: currentUser.email,
            status: 'em_analise',
            created_at: now
          });
          if (insertError) console.warn('Insert empresa:', insertError);
        }
      } else {
        const { data: existe } = await supabase
          .from('motoristas')
          .select('id')
          .eq('usuario_id', currentUser.id)
          .maybeSingle();

        if (!existe) {
          const { error: insertError } = await supabase.from('motoristas').insert({
            usuario_id: currentUser.id,
            nome_completo: currentUser.user_metadata?.full_name || 'Motorista',
            status: 'em_analise',
            created_at: now
          });
          if (insertError) console.warn('Insert motorista:', insertError);
        }
      }

      await refreshUser();
      await new Promise(resolve => setTimeout(resolve, 500));

      // ✅ NAVEGAÇÃO COM RECARREGAMENTO (PRESERVADO)
      const destino = safeTipo === 'motorista' ? '/cadastro-motorista' : '/cadastro-empresa';
      console.log(`[SelecionarTipo] Navegando para ${destino} com reload...`);
      window.location.href = destino;

    } catch (err) {
      console.error('[SelecionarTipo] Erro:', err);
      setError(err.message || 'Erro ao selecionar tipo. Tente novamente.');
      setLoading(null);
    }
  };

  // ==========================================
  // 🎨 LOADING LENDÁRIO
  // ==========================================
  if (!currentUser && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E3A5F] to-[#0F172A] flex items-center justify-center relative overflow-hidden">
        {/* Partículas animadas */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-[#FF6B35] rounded-full animate-ping opacity-20"></div>
          <div className="absolute top-3/4 right-1/3 w-3 h-3 bg-[#FF6B35] rounded-full animate-ping opacity-15 delay-300"></div>
          <div className="absolute top-1/2 left-2/3 w-2 h-2 bg-[#FFA07A] rounded-full animate-ping opacity-25 delay-700"></div>
        </div>
        
        <div className="text-center relative z-10">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-[#FF6B35] rounded-full blur-xl opacity-30 animate-pulse"></div>
            <Loader2 className="w-16 h-16 text-[#FF6B35] animate-spin mx-auto relative" />
          </div>
          <p className="text-[#FFA07A] text-lg font-medium animate-pulse">Verificando sessão...</p>
          <p className="text-slate-500 text-sm mt-2">Preparando seu acesso lendário</p>
        </div>
      </div>
    );
  }

  // ==========================================
  // 🎨 MAIN LENDÁRIO
  // ==========================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E3A5F] to-[#0F172A] 
      flex items-center justify-center p-4 relative overflow-hidden">
      
      {/* Background animado */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradientes flutuantes */}
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[#FF6B35]/10 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#1E3A5F]/20 rounded-full blur-[100px] animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FF6B35]/5 rounded-full blur-[120px] animate-pulse delay-500"></div>
        
        {/* Linhas decorativas */}
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          <div className="absolute top-20 left-10 w-40 h-[1px] bg-gradient-to-r from-[#FF6B35] to-transparent"></div>
          <div className="absolute top-40 right-20 w-60 h-[1px] bg-gradient-to-l from-[#FF6B35] to-transparent"></div>
          <div className="absolute bottom-32 left-1/4 w-32 h-[1px] bg-gradient-to-r from-[#FFA07A] to-transparent"></div>
        </div>
      </div>

      <div className="max-w-5xl w-full relative z-10">
        
        {/* ==========================================
            HEADER LENDÁRIO
            ========================================== */}
        <div className="text-center mb-12">
          {/* Badge animado */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF6B35]/10 rounded-full border border-[#FF6B35]/30 mb-8 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-[#FF6B35] animate-spin-slow" />
            <span className="text-[#FFA07A] text-sm font-medium tracking-wide uppercase">Escolha seu perfil</span>
            <span className="w-2 h-2 bg-[#FF6B35] rounded-full animate-pulse"></span>
          </div>
          
          {/* Logo Borinho */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-[#FF6B35] rounded-full blur-2xl opacity-20 animate-pulse"></div>
              <img 
                src="/logoborinho45.png" 
                alt="BoraFrete" 
                className="h-24 w-auto relative drop-shadow-[0_4px_20px_rgba(255,107,53,0.3)]"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center justify-center">
                <Truck className="w-12 h-12 text-[#FF6B35]" />
              </div>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4 tracking-tight">
            Bem-vindo ao{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] via-[#FFA07A] to-[#FF6B35] animate-gradient">
              BoraFrete
            </span>
          </h1>
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Escolha como você quer fazer parte da{' '}
            <span className="text-[#FF6B35] font-semibold">maior rede de fretes</span>{' '}
            do Brasil
          </p>
        </div>

        {/* ==========================================
            ERRO
            ========================================== */}
        {error && (
          <div className="mb-8 p-5 bg-red-500/10 border border-red-500/30 rounded-2xl text-red-300 text-center backdrop-blur-sm max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* ==========================================
            CARDS LENDÁRIOS
            ========================================== */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          
          {/* ===== MOTORISTA ===== */}
          <button
            onClick={() => handleSelecionarTipo('motorista')}
            onMouseEnter={() => setHovered('motorista')}
            onMouseLeave={() => setHovered(null)}
            disabled={loading !== null}
            className={`group relative rounded-3xl p-8 transition-all duration-700 text-left overflow-hidden
              ${hovered === 'motorista' 
                ? 'bg-gradient-to-br from-[#FF6B35]/20 via-[#1E3A5F]/40 to-[#0F172A]/60 scale-[1.03] shadow-[0_20px_60px_rgba(255,107,53,0.2)] border-2 border-[#FF6B35]/50' 
                : 'bg-gradient-to-br from-slate-800/40 via-slate-800/20 to-transparent border-2 border-slate-700/30 shadow-[0_10px_30px_rgba(0,0,0,0.3)]'}
              ${loading === 'motorista' ? 'opacity-70' : 'hover:shadow-[0_25px_70px_rgba(255,107,53,0.25)]'}
              disabled:cursor-not-allowed`}
          >
            {/* Glow effect no hover */}
            <div className={`absolute inset-0 bg-gradient-to-br from-[#FF6B35]/10 to-transparent transition-opacity duration-500
              ${hovered === 'motorista' ? 'opacity-100' : 'opacity-0'}`}></div>
            
            <div className="relative z-10 flex flex-col h-full">
              
              {/* Header do card */}
              <div className="flex items-start justify-between mb-8">
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-500 relative
                  ${hovered === 'motorista' ? 'bg-gradient-to-br from-[#FF6B35] to-[#FFA07A] shadow-[0_10px_40px_rgba(255,107,53,0.4)] scale-110' : 'bg-[#FF6B35]/10'}`}>
                  <Truck className={`w-10 h-10 transition-all duration-500
                    ${hovered === 'motorista' ? 'text-white' : 'text-[#FF6B35]'}`} />
                  
                  {/* Badge "Popular" */}
                  <div className={`absolute -top-2 -right-2 bg-gradient-to-r from-[#FF6B35] to-[#FFA07A] text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg transition-all duration-300
                    ${hovered === 'motorista' ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
                    POPULAR
                  </div>
                </div>
                
                <ArrowRight className={`w-8 h-8 transition-all duration-500
                  ${hovered === 'motorista' ? 'text-[#FF6B35] translate-x-1' : 'text-slate-600'}`} />
              </div>

              {/* Título */}
              <h2 className="text-3xl font-black text-white mb-3 group-hover:text-[#FFA07A] transition-colors duration-300">
                Sou Motorista
              </h2>
              
              {/* Descrição */}
              <p className="text-slate-400 mb-6 leading-relaxed text-base">
                Encontre cargas disponíveis, faça fretes e{' '}
                <span className="text-[#FF6B35] font-semibold">aumente sua renda</span>. 
                Conecte-se com empresas que precisam de transporte em todo o Brasil.
              </p>

              {/* Features */}
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FFA07A] text-xs font-medium">
                  <Zap className="w-3 h-3" /> Cargas em tempo real
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FFA07A] text-xs font-medium">
                  <TrendingUp className="w-3 h-3" /> Renda extra
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF6A7A]/10 border border-[#FF6B35]/20 rounded-full text-[#FFA07A] text-xs font-medium">
                  <MapPin className="w-3 h-3" /> Todo Brasil
                </span>
              </div>

              {/* Imagem */}
              <div className="mt-auto">
                <div className="relative h-52 rounded-2xl overflow-hidden mb-5 group">
                  <img src="/motorista123.jpg" alt="Motorista" 
                    className={`w-full h-full object-cover transition-all duration-700 ease-out
                      ${hovered === 'motorista' ? 'scale-110 brightness-110' : 'scale-100 brightness-90'}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-transparent"></div>
                  
                  {/* Overlay no hover */}
                  <div className={`absolute inset-0 bg-[#FF6B35]/20 transition-opacity duration-500 flex items-center justify-center
                    ${hovered === 'motorista' ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="bg-white/20 backdrop-blur-md rounded-full p-4 border border-white/30">
                      <ArrowRight className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
                
                {/* CTA */}
                <div className={`flex items-center gap-3 transition-all duration-500
                  ${hovered === 'motorista' ? 'translate-x-2' : ''}`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500
                    ${hovered === 'motorista' ? 'bg-gradient-to-r from-[#FF6B35] to-[#FFA07A] shadow-lg' : 'bg-[#FF6B35]/20'}`}>
                    <CheckCircle2 className={`w-6 h-6 transition-colors
                      ${hovered === 'motorista' ? 'text-white' : 'text-[#FF6B35]'}`} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Cadastro gratuito</p>
                    <p className="text-slate-500 text-xs">Comece a rodar hoje</p>
                  </div>
                </div>
              </div>

              {/* Loading overlay */}
              {loading === 'motorista' && (
                <div className="absolute inset-0 bg-[#0F172A]/90 rounded-3xl flex flex-col items-center justify-center backdrop-blur-md z-20">
                  <div className="relative mb-4">
                    <div className="absolute inset-0 bg-[#FF6B35] rounded-full blur-xl opacity-30 animate-pulse"></div>
                    <Loader2 className="w-12 h-12 text-[#FF6B35] animate-spin relative" />
                  </div>
                  <span className="text-[#FFA07A] font-bold text-lg">Configurando perfil...</span>
                  <span className="text-slate-500 text-sm mt-1">Preparando seu acesso</span>
                </div>
              )}
            </div>
          </button>

          {/* ===== EMPRESA ===== */}
          <button
            onClick={() => handleSelecionarTipo('empresa')}
            onMouseEnter={() => setHovered('empresa')}
            onMouseLeave={() => setHovered(null)}
            disabled={loading !== null}
            className={`group relative rounded-3xl p-8 transition-all duration-700 text-left overflow-hidden
              ${hovered === 'empresa' 
                ? 'bg-gradient-to-br from-[#1E3A5F]/40 via-[#0F172A]/60 to-[#0F172A]/80 scale-[1.03] shadow-[0_20px_60px_rgba(30,58,95,0.3)] border-2 border-[#1E3A5F]/50' 
                : 'bg-gradient-to-br from-slate-800/40 via-slate-800/20 to-transparent border-2 border-slate-700/30 shadow-[0_10px_30px_rgba(0,0,0,0.3)]'}
              ${loading === 'empresa' ? 'opacity-70' : 'hover:shadow-[0_25px_70px_rgba(30,58,95,0.35)]'}
              disabled:cursor-not-allowed`}
          >
            {/* Glow effect no hover */}
            <div className={`absolute inset-0 bg-gradient-to-br from-[#1E3A5F]/10 to-transparent transition-opacity duration-500
              ${hovered === 'empresa' ? 'opacity-100' : 'opacity-0'}`}></div>
            
            <div className="relative z-10 flex flex-col h-full">
              
              {/* Header do card */}
              <div className="flex items-start justify-between mb-8">
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-500 relative
                  ${hovered === 'empresa' ? 'bg-gradient-to-br from-[#1E3A5F] to-[#3B5998] shadow-[0_10px_40px_rgba(30,58,95,0.4)] scale-110' : 'bg-[#1E3A5F]/10'}`}>
                  <Building2 className={`w-10 h-10 transition-all duration-500
                    ${hovered === 'empresa' ? 'text-white' : 'text-[#1E3A5F]'}`} />
                  
                  {/* Badge "Novo" */}
                  <div className={`absolute -top-2 -right-2 bg-gradient-to-r from-[#1E3A5F] to-[#3B5998] text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg transition-all duration-300
                    ${hovered === 'empresa' ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
                    NOVO
                  </div>
                </div>
                
                <ArrowRight className={`w-8 h-8 transition-all duration-500
                  ${hovered === 'empresa' ? 'text-[#1E3A5F] translate-x-1' : 'text-slate-600'}`} />
              </div>

              {/* Título */}
              <h2 className="text-3xl font-black text-white mb-3 group-hover:text-[#7EB8FF] transition-colors duration-300">
                Sou Empresa
              </h2>
              
              {/* Descrição */}
              <p className="text-slate-400 mb-6 leading-relaxed text-base">
                Cadastre suas cargas e encontre{' '}
                <span className="text-[#7EB8FF] font-semibold">motoristas confiáveis</span>. 
                Gestão completa de fretes e transportes para sua empresa.
              </p>

              {/* Features */}
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1E3A5F]/20 border border-[#1E3A5F]/30 rounded-full text-[#7EB8FF] text-xs font-medium">
                  <Shield className="w-3 h-3" /> Motoristas verificados
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1E3A5F]/20 border border-[#1E3A5F]/30 rounded-full text-[#7EB8FF] text-xs font-medium">
                  <Clock className="w-3 h-3" /> 24h disponível
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1E3A5F]/20 border border-[#1E3A5F]/30 rounded-full text-[#7EB8FF] text-xs font-medium">
                  <TrendingUp className="w-3 h-3" /> Gestão completa
                </span>
              </div>

              {/* Imagem */}
              <div className="mt-auto">
                <div className="relative h-52 rounded-2xl overflow-hidden mb-5 group">
                  <img src="/empresa12.jpg" alt="Empresa" 
                    className={`w-full h-full object-cover transition-all duration-700 ease-out
                      ${hovered === 'empresa' ? 'scale-110 brightness-110' : 'scale-100 brightness-90'}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-transparent"></div>
                  
                  {/* Overlay no hover */}
                  <div className={`absolute inset-0 bg-[#1E3A5F]/20 transition-opacity duration-500 flex items-center justify-center
                    ${hovered === 'empresa' ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="bg-white/20 backdrop-blur-md rounded-full p-4 border border-white/30">
                      <ArrowRight className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
                
                {/* CTA */}
                <div className={`flex items-center gap-3 transition-all duration-500
                  ${hovered === 'empresa' ? 'translate-x-2' : ''}`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500
                    ${hovered === 'empresa' ? 'bg-gradient-to-r from-[#1E3A5F] to-[#3B5998] shadow-lg' : 'bg-[#1E3A5F]/20'}`}>
                    <CheckCircle2 className={`w-6 h-6 transition-colors
                      ${hovered === 'empresa' ? 'text-white' : 'text-[#1E3A5F]'}`} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Publique cargas grátis</p>
                    <p className="text-slate-500 text-xs">Encontre motoristas agora</p>
                  </div>
                </div>
              </div>

              {/* Loading overlay */}
              {loading === 'empresa' && (
                <div className="absolute inset-0 bg-[#0F172A]/90 rounded-3xl flex flex-col items-center justify-center backdrop-blur-md z-20">
                  <div className="relative mb-4">
                    <div className="absolute inset-0 bg-[#1E3A5F] rounded-full blur-xl opacity-30 animate-pulse"></div>
                    <Loader2 className="w-12 h-12 text-[#1E3A5F] animate-spin relative" />
                  </div>
                  <span className="text-[#7EB8FF] font-bold text-lg">Configurando perfil...</span>
                  <span className="text-slate-500 text-sm mt-1">Preparando seu acesso</span>
                </div>
              )}
            </div>
          </button>
        </div>

        {/* ==========================================
            FOOTER
            ========================================== */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/30 rounded-full border border-slate-700/30">
            <Shield className="w-4 h-4 text-[#FF6B35]" />
            <p className="text-slate-400 text-sm">
              Você pode alterar seu tipo de perfil depois nas{' '}
              <span className="text-[#FFA07A] font-medium">configurações</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}